import { useMemo } from "react";
import { get } from "lodash";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../../../utils/hooks/user";
import { useCompany } from "../../../../utils/hooks/company";
import useApi from "../../../../utils/api";
import { companyPreferenceQueryOptions, userPreferenceQueryOptions } from "../api/usePreferenceQuery";

const formatPreferences = (data: any) => {
    return (data || [])?.reduce((total: any, item: any) => ({
        ...total,
        [item.preferenceKey]: item.config,
    }), {});
}

const isPlainObject = (v: any): v is Record<string, any> =>
    v !== null && typeof v === "object" && !Array.isArray(v);

const mergeArraysByAccessor = (cArr: any[], uArr: any[]): any[] => {
    const userMap = Object.fromEntries(
        uArr.filter((i: any) => i.accessor).map((i: any) => [i.accessor, i])
    );

    return cArr.map((cItem: any) => {
        if (!cItem.accessor) return cItem;
        const uItem = userMap[cItem.accessor];
        if (!uItem) return cItem;
        return {
            ...uItem,
            hideCompletely: cItem.hideCompletely,
            hideSort: cItem.hideSort,
        };
    });
};

const hasAccessorItems = (arr: any[]) => arr.some((i: any) => i?.accessor);

const deepMerge = (cVal: any, uVal: any): any => {
    if (Array.isArray(cVal) || Array.isArray(uVal)) {
        if (!uVal) return cVal ?? [];
        if (!cVal) return uVal ?? [];
        if (hasAccessorItems(cVal) || hasAccessorItems(uVal)) {
            return mergeArraysByAccessor(cVal, uVal);
        }
        return uVal;
    }

    if (isPlainObject(cVal) && isPlainObject(uVal)) {
        const result: Record<string, any> = { ...cVal };
        for (const [key, uv] of Object.entries(uVal)) {
            if (uv == null) continue;
            result[key] = deepMerge(cVal[key], uv);
        }
        return result;
    }

    return uVal ?? cVal;
};

// export const usePreferencesFetched = () => {
//     const queryClient = useQueryClient();
//     const user = useUser();
//     const company = useCompany();
//     const userData = queryClient.getQueryData(preferenceQueryKeys.user(user?.id as string));
//     const companyData = queryClient.getQueryData(preferenceQueryKeys.company(company?.id as string));
//     return userData !== undefined && companyData !== undefined;
// };

export const useUserPreferences = (name: string) => {
    const user = useUser();
    const { authGet } = useApi();
    const { data }: any = useQuery(userPreferenceQueryOptions(authGet, user?.id));
    return useMemo(() => get(formatPreferences(data?.result), name), [data, name]);
};

export const useCompanyPreferences = (name: string) => {
    const company = useCompany();
    const { authGet } = useApi();
    const { data }: any = useQuery(companyPreferenceQueryOptions(authGet, company?.id));
    return useMemo(() => get(formatPreferences(data?.result), name), [data, name]);
};

export const usePreferences = (name: string) => {
    const uData = useUserPreferences(name);
    const cData = useCompanyPreferences(name);
    return useMemo(() => deepMerge(cData, uData), [cData, uData]);
};
